import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { syncAgencyTimezone } from '../utils/agencyTimezone';
import { authSlice, AuthSlice } from './slices/authSlice';
import { appSlice, AppSlice } from './slices/appSlice';
import { patientsSlice, PatientsSlice } from './slices/patientsSlice';
import { documentsSlice, DocumentsSlice } from './slices/documentsSlice';
import { tasksSlice, TasksSlice } from './slices/tasksSlice';
import { intakeSlice, IntakeSlice } from './slices/intakeSlice';
import { toastSlice, ToastSlice } from './slices/toastSlice';
import { uploadsSlice, UploadsSlice } from './slices/uploadsSlice';
import { payorSlice, PayorSlice } from './slices/payorSlice';
import { rogerDocSlice, RogerDocSlice } from './slices/rogerDocSlice';
import {
  agencyInsuranceSlice,
  AgencyInsuranceSlice,
} from './slices/agencyInsuranceSlice';
import {
  agencyClinicSlice,
  AgencyClinicSlice,
} from './slices/agencyClinicSlice';
import {
  dashboardClinicSlice,
  DashboardClinicSlice,
} from './slices/dashboardClinicSlice';
import {
  noaSlice,
  NOASlice,
  migrateNoaManagerFilters,
  type NOAManagerFilters,
} from './slices/noaSlice';
import {
  oasisManagerSlice,
  OASISManagerSlice,
} from './slices/oasisManagerSlice';
import { payrollSlice, PayrollSlice } from './slices/payrollSlice';
import {
  formSchemaLayoutGenerationSlice,
  FormSchemaLayoutGenerationSlice,
} from './slices/formSchemaLayoutGenerationSlice';
import {
  formSchemaEditorSlice,
  FormSchemaEditorSlice,
} from './slices/formSchemaEditorSlice';
import {
  vibecoderSettingsSlice,
  VibecoderSettingsSlice,
} from './slices/vibecoderSettingsSlice';
import {
  billingUiSlice,
  BillingUiSlice,
  migrateBillingUiState,
  type BillingTabKey,
  type BillingTabUiState,
} from './slices/billingUiSlice';
import { chatSlice, ChatSlice } from './slices/chatSlice';

export type StoreState = AuthSlice &
  AppSlice &
  PatientsSlice &
  DocumentsSlice &
  TasksSlice &
  IntakeSlice &
  ToastSlice &
  UploadsSlice &
  PayorSlice &
  RogerDocSlice &
  AgencyInsuranceSlice &
  AgencyClinicSlice &
  DashboardClinicSlice &
  NOASlice &
  OASISManagerSlice &
  PayrollSlice &
  FormSchemaLayoutGenerationSlice &
  FormSchemaEditorSlice &
  VibecoderSettingsSlice &
  BillingUiSlice &
  ChatSlice;

export const useStore = create<StoreState>()(
  devtools(
    persist(
      (...a) => ({
        ...authSlice(...a),
        ...appSlice(...a),
        ...patientsSlice(...a),
        ...documentsSlice(...a),
        ...tasksSlice(...a),
        ...intakeSlice(...a),
        ...toastSlice(...a),
        ...uploadsSlice(...a),
        ...payorSlice(...a),
        ...rogerDocSlice(...a),
        ...agencyInsuranceSlice(...a),
        ...agencyClinicSlice(...a),
        ...dashboardClinicSlice(...a),
        ...noaSlice(...a),
        ...oasisManagerSlice(...a),
        ...payrollSlice(...a),
        ...formSchemaLayoutGenerationSlice(...a),
        ...formSchemaEditorSlice(...a),
        ...vibecoderSettingsSlice(...a),
        ...billingUiSlice(...a),
        ...chatSlice(...a),
      }),
      {
        name: 'roger-emr-store',
        version: 5,
        migrate: (persisted: unknown, fromVersion: number) => {
          const state = persisted as {
            billingUiState?: Record<string, BillingTabUiState>;
            noaManagerFilters?: Partial<NOAManagerFilters>;
            intakeManagerFilters?: { startDate?: string; endDate?: string };
            oasisManagerFilters?: { hideImportedPatients?: boolean };
          };
          // v2 (ROG-2913): billing tabs became server-driven — drop legacy
          // client-side columnFilters and ensure insuranceIds/clinicIds exist.
          if (fromVersion < 2 && state?.billingUiState) {
            state.billingUiState = migrateBillingUiState(
              state.billingUiState as Partial<
                Record<BillingTabKey, BillingTabUiState>
              >
            );
          }
          // v3 (EMR-1528): the Missing NOAs tab is gone; a persisted
          // selection of it would render an empty table.
          if (fromVersion < 3 && state?.noaManagerFilters) {
            state.noaManagerFilters = migrateNoaManagerFilters(
              state.noaManagerFilters
            );
          }
          // v4: Intake Manager used to seed and save today as the end date,
          // which on a later day hid every newer intake. A saved end date cannot
          // be told apart from a picked one, so drop it; the default end date is
          // now empty. A cleared ('') end date and the start date stay.
          const intakeFilters = state?.intakeManagerFilters;
          if (fromVersion < 4 && intakeFilters?.endDate) {
            delete intakeFilters.endDate;
          }
          // v5: hide imported patients by default for persisted OASIS Manager filters.
          if (
            fromVersion < 5 &&
            state?.oasisManagerFilters &&
            state.oasisManagerFilters.hideImportedPatients === undefined
          ) {
            state.oasisManagerFilters.hideImportedPatients = true;
          }
          return state as never;
        },
        partialize: state => ({
          user: state.user,
          isAuthenticated: state.isAuthenticated,
          isSidebarCollapsed: state.isSidebarCollapsed,
          intakeManagerFilters: state.intakeManagerFilters,
          noaManagerFilters: state.noaManagerFilters,
          oasisManagerFilters: state.oasisManagerFilters,
          vibecoderModelId: state.vibecoderModelId,
          vibecoderTemperature: state.vibecoderTemperature,
          billingUiState: state.billingUiState,
          hideTestPatients: state.hideTestPatients,
          hideImportedPatients: state.hideImportedPatients,
          patientManagerHideImportedPatients:
            state.patientManagerHideImportedPatients,
          showInactiveEpisodes: state.showInactiveEpisodes,
          remittanceUiState: state.remittanceUiState,
          clinicFilterSelections: state.clinicFilterSelections,
        }),
      }
    ),
    {
      name: 'roger-emr-store',
    }
  )
);

// Keep the date helpers on the signed-in agency's clock.
syncAgencyTimezone(useStore);
